import { useState } from 'react'
import { formatMetric, METRIC_LABEL } from '../lib/performance.js'
import { useAddLink, useRemoveLink } from '../lib/items.js'
import type { ItemDetail, LinkKind } from '../lib/items.js'
import { Icon } from './Icon.js'

const KIND_LABEL: Record<LinkKind, string> = {
  brevo_campaign: 'Brevo-Kampagne',
  upload_post_job: 'Upload-Post',
  social_post: 'Post',
  file: 'Datei',
  url: 'Link',
}

/** Figures worth showing first, per kind of source. */
const HEADLINE = ['openRate', 'clickRate', 'reach', 'views', 'likes', 'saves', 'clicks', 'opens']

function href(kind: LinkKind, ref: string): string | undefined {
  if (/^https:\/\//.test(ref)) return ref
  if (kind === 'brevo_campaign' && /^\d+$/.test(ref)) return `https://app.brevo.com/marketing-campaign/edit/${ref}`
  return undefined
}

/** Where the piece lives once it is out — and how it did. */
export function ItemLinks({ item }: { item: ItemDetail }): JSX.Element {
  const add = useAddLink()
  const remove = useRemoveLink()
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<LinkKind>('url')
  const [ref, setRef] = useState('')

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <p className="section-title px-0">Verknüpfungen &amp; Zahlen</p>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-small text-pebro">
          {open ? 'Fertig' : 'Hinzufügen'}
        </button>
      </div>

      {item.links.length === 0 && !open && (
        <p className="rounded-[14px] bg-fill/[0.06] px-4 py-3 text-small text-label2/60">
          Brevo-Kampagne oder veröffentlichten Post verknüpfen. Claude trägt die Zahlen dazu ein.
        </p>
      )}

      <div className="space-y-2">
        {item.links.map((l) => {
          const url = href(l.kind, l.ref)
          const figures = l.metrics ? HEADLINE.filter((k) => l.metrics![k] !== undefined).slice(0, 4) : []
          return (
            <div key={l.id} className="rounded-[14px] bg-fill/[0.06] px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-caption font-semibold uppercase tracking-wide text-label2/50">{KIND_LABEL[l.kind]}</span>
                {url ? (
                  <a href={url} target="_blank" rel="noreferrer noopener" className="min-w-0 flex-1 truncate text-small text-pebro">
                    {l.label || l.ref}
                  </a>
                ) : (
                  <span className="min-w-0 flex-1 truncate text-small">{l.label || l.ref}</span>
                )}
                <button type="button" aria-label="Verknüpfung entfernen" onClick={() => remove.mutate(l.id)} className="text-label2/40 hover:text-danger">
                  <Icon name="close" size={14} />
                </button>
              </div>
              {figures.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
                  {figures.map((k) => (
                    <div key={k}>
                      <p className="num text-callout font-semibold">{formatMetric(k, l.metrics![k]!)}</p>
                      <p className="text-caption text-label2/55">{METRIC_LABEL[k] ?? k}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {open && (
        <form
          className="mt-2 flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault()
            if (!ref.trim()) return
            add.mutate({ itemId: item.id, kind, ref: ref.trim() }, { onSuccess: () => setRef('') })
          }}
        >
          <select className="field min-h-[42px] text-small sm:w-40" value={kind} onChange={(e) => setKind(e.target.value as LinkKind)}>
            {(Object.keys(KIND_LABEL) as LinkKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
          <input
            className="field min-h-[42px] flex-1 text-small"
            placeholder={kind === 'brevo_campaign' ? 'Kampagnen-ID, z. B. 412' : 'https://…'}
            value={ref}
            onChange={(e) => setRef(e.target.value)}
          />
          <button type="submit" className="btn-ghost min-h-[42px]" disabled={!ref.trim() || add.isPending}>
            Verknüpfen
          </button>
        </form>
      )}
    </section>
  )
}
